import type {CSSProperty, CSSValue, EnrichedAstNode} from '../types';
import {extractUnitAndValue} from '../UnitConversion/utils/extractUnitAndValue';

export interface StyleCheckerExistence {
    key: CSSProperty;
    existence: true;
    nonZero?: boolean;
    value?: never;
}

export interface StyleCheckerValue<K extends CSSProperty> {
    key: CSSProperty;
    existence?: never;
    value: CSSValue<K> | Array<CSSValue<K>>;
}

export type StyleChecker<K extends CSSProperty> = StyleCheckerExistence | StyleCheckerValue<K>;

export const checkForOneStyle = <T extends CSSProperty, S extends StyleChecker<T>>(
    node: EnrichedAstNode,
    styles: Array<S>
): boolean =>
    styles.some((style) => {
        if (style.existence) {
            if (!Object.prototype.hasOwnProperty.call(node.styles, style.key)) {
                return false;
            }
            return true;
        }

        if (node.styles[style.key] !== style.value) {
            return false;
        }
        return true;
    });

export const checkForAllStyles = <T extends CSSProperty>(
    node: EnrichedAstNode,
    styles: Array<StyleChecker<T>>
): boolean =>
    styles.every((style) => {
        if (style.existence) {
            if (!Object.prototype.hasOwnProperty.call(node.styles, style.key)) {
                return false;
            }
            if (style.nonZero && extractUnitAndValue(node.styles[style.key]).value === 0) {
                return false;
            }
            return true;
        }

        if (node.styles[style.key] !== style.value) {
            return false;
        }
        return true;
    });
